import React from "react";
import {
  createStyles,
  Box,
  Stack,
  Text,
  Center,
  Image,
  Space,
  Group,
  Anchor,
  List,
} from "@mantine/core";

import { Link, useNavigate } from "react-router-dom";

function PrivacyPage({ children, ...otherProps }) {
  const { classes } = useStyles();
  const navigation = useNavigate();

  return (
    <Box className={classes.container} {...otherProps}>
      <Box className={classes.root}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center px={50} pt={30} pb={10}>
              <Stack spacing={0} justify="center">
                <Center>
                  <Group spacing={10} align="center" sx={{ cursor: "pointer" }} onClick={() => navigation("/")}>
                    <Image
                      src="/mineralis-ico.svg"
                      alt="Mineralis"
                      width={48}
                      height={48}
                      sx={{ objectFit: "contain" }}
                    />
                    <Text weight={800} size={30} sx={{ color: "#6A1089" }}>
                      Mineralis
                    </Text>
                  </Group>
                </Center>
              </Stack>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      <Box className={classes.containerTwo}>
        <Box className={classes.root}>
          <Box className={classes.grid}>
            <Box className={classes.section} mt={50}>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  Privacy Policy
                </Text>
                <Text weight={400} size={18} fs="italic">
                  Last Updated: April 18, 2026
                </Text>
                <Text weight={400} size={18}>
                  This Privacy Policy explains how information is collected, used, and disclosed by
                  Mineralis with respect to your access and use of our iOS application (referred to
                  below as "Mineralis", "we", "us", or "our").
                </Text>
                <br />
                <Text weight={400} size={18}>
                  We are committed to protecting your privacy and being transparent about our data
                  practices. This policy describes what data we collect, how we use it, and your
                  rights regarding your personal information.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  1. Data We Collect
                </Text>
                <Text weight={400} size={18} mt={10}>
                  We collect the following types of data when you use Mineralis:
                </Text>

                <Text weight={600} size={20} mt={15}>
                  1.1 User Identifier (User ID)
                </Text>
                <Text weight={400} size={18}>
                  We generate and store a unique random identifier (UUID) on your device when you
                  first launch the app. This identifier:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Is stored securely in your device's Keychain
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Persists across app sessions to provide continuity
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Is NOT the device advertising identifier (IDFA)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Is used to link your usage data for analytics purposes
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  1.2 Product Interaction Data
                </Text>
                <Text weight={400} size={18}>
                  We collect data about how you interact with the app's features, including:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      App launches, feature usage, and navigation patterns
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Rock and mineral scanning, identification results viewed, and collection management interactions
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      AI Mineralogist chat usage and session interactions
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Settings changes and premium feature usage
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  1.3 Camera and Photo Access
                </Text>
                <Text weight={400} size={18}>
                  Mineralis uses your device camera and photo library solely to perform AI-powered rock and mineral identification. We want to be clear:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Photos you take or select are processed locally and/or sent to our AI backend for identification
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Images are NOT stored on our servers beyond the immediate processing request
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Camera access is only activated when you explicitly initiate a scan
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      You can revoke camera permissions at any time in iOS Settings → Mineralis
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  1.4 Purchase History
                </Text>
                <Text weight={400} size={18}>
                  We collect information about your subscription and in-app purchases, including:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Subscription type, trial status, and renewal information
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Purchase dates, product IDs, and transaction details
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Subscription lifecycle events (started, renewed, expired, cancelled)
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  1.5 Apple Search Ads Attribution Data
                </Text>
                <Text weight={400} size={18}>
                  When you install Mineralis from an Apple Search Ads campaign, we collect attribution
                  data through Apple's AdServices framework, including:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Campaign keywords and campaign identifiers
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Ad group and creative set information
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Attribution source (Apple Search Ads vs. organic install)
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18}>
                  <strong>Important:</strong> Apple Search Ads Attribution is a first-party Apple
                  service that does not require App Tracking Transparency (ATT) permission. This
                  attribution helps us measure the effectiveness of our advertising campaigns.
                </Text>

                <Text weight={600} size={20} mt={15}>
                  1.6 Device Advertising Identifier (IDFA) and App Tracking Transparency (ATT)
                </Text>
                <Text weight={400} size={18}>
                  Mineralis may collect your device's advertising identifier (IDFA) to measure the
                  effectiveness of our advertising campaigns and provide more accurate attribution.
                </Text>
                <Text weight={400} size={18} mt={10}>
                  <strong>App Tracking Transparency (ATT) Permission:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      When you first launch Mineralis, you will see a prompt asking for permission
                      to track your activity across apps and websites owned by other companies
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      If you <strong>allow</strong> tracking, we may access your IDFA for
                      advertising attribution and analytics purposes
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      If you <strong>deny</strong> tracking, we will NOT access your IDFA, and
                      advertising attribution will rely solely on SKAdNetwork (see section 1.7)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      You can change your choice at any time in your iOS Settings {"->"} Privacy{" "}
                      {"->"} Tracking
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>How We Use IDFA (with your permission):</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Measure the effectiveness of our advertising campaigns (Google Ads, Apple
                      Search Ads)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Attribute app installs and key in-app actions to specific ads and campaigns
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Analyze user behavior and conversion funnels to improve our advertising
                      efficiency
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>What We Do NOT Use IDFA For:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Cross-app tracking for behavioral profiling
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Selling your data to third-party data brokers
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Combining with data from other apps or websites for targeted advertising
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>Important:</strong> Your choice regarding ATT permission is entirely
                  yours. If you deny tracking, Mineralis will continue to function normally, and we
                  will use SKAdNetwork for privacy-preserving attribution.
                </Text>

                <Text weight={600} size={20} mt={15}>
                  1.7 SKAdNetwork Conversion Data
                </Text>
                <Text weight={400} size={18}>
                  Mineralis uses Apple's SKAdNetwork framework to measure the effectiveness of our
                  advertising campaigns, including Google Ads. SKAdNetwork allows us to understand
                  which campaigns lead to app installs and conversions without collecting personal
                  information.
                </Text>
                <Text weight={400} size={18} mt={10}>
                  <strong>What SKAdNetwork Collects:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Conversion Values:</strong> Numerical values (0–63) that indicate user
                      actions such as: app installation, trial start, first purchase, subscription
                      renewal, and high-value user status
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Coarse Values:</strong> Broad categories (low/medium/high) that group
                      conversion values for privacy (SKAN 4.0+)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Postback Timing:</strong> When conversion events occur (timer starts
                      at install)
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>What SKAdNetwork Does NOT Collect:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      No personal identifiers (name, email, device ID)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      No device advertising identifier (IDFA)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      No precise timestamps or user location
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      No linkable data that can identify you personally
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>Important:</strong> SKAdNetwork is a privacy-preserving attribution
                  system designed by Apple. It does NOT require ATT permission because it does not
                  collect or share any user-identifiable information.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  2. Data We Do NOT Collect
                </Text>
                <Text weight={400} size={18}>
                  We want to be clear about what we <strong>do not</strong> collect:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Device Advertising Identifier (IDFA):</strong> We do NOT collect your
                      IDFA unless you explicitly grant permission through the ATT prompt. If you deny
                      tracking, your IDFA is never accessed or collected
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Collection & Notes Content:</strong> Your saved stones, mineral notes,
                      and wishlists are stored locally on your device and are NOT sent to our servers
                      or analytics services
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Personal Contact Information:</strong> We do not collect your name,
                      email, phone number, or address (unless you voluntarily provide it when
                      contacting support)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Location Data:</strong> We do not collect your geographic location
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Chat Message Content:</strong> We track that you used the AI
                      Mineralogist chat feature, but we do not store your conversation messages on
                      our analytics servers
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Scanned Images:</strong> Photos used for identification are not
                      permanently stored on our servers after processing
                    </Text>
                  </List.Item>
                </List>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  3. How We Use Your Data
                </Text>
                <Text weight={400} size={18}>
                  We use the collected data for the following purposes:
                </Text>

                <Text weight={600} size={20} mt={15}>
                  3.1 Analytics
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Understand how users interact with Mineralis's features
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Measure feature adoption and identify areas for improvement
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Track subscription conversion funnel and user retention
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Measure the effectiveness of our Apple Search Ads campaigns
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Measure the effectiveness of our Google Ads campaigns through SKAdNetwork
                      attribution
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  3.2 App Functionality
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Process your photos through AI to identify rocks, minerals, crystals, and gemstones
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Enable premium features for subscribers
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Restore purchases across your devices
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Manage subscription lifecycle and access control
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Provide customer support and troubleshoot issues
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  3.3 What We Do NOT Use Your Data For
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Third-Party Advertising:</strong> We do not share your data with
                      advertisers or ad networks
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Cross-App or Cross-Site Tracking:</strong> We do not track your
                      activity across other apps or websites
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Marketing Communications:</strong> We do not use your data to send
                      marketing emails or push notifications (unless you explicitly opt-in)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Selling Data:</strong> We never sell your personal data to third
                      parties
                    </Text>
                  </List.Item>
                </List>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  4. Data Linking and Tracking
                </Text>
                <Text weight={400} size={18}>
                  <strong>Data Linked to Your Identity:</strong> All collected data (User ID,
                  Product Interaction, Purchase History) is linked to your user identity via the
                  unique UUID we generate. This allows us to connect your usage events over time to
                  provide better analytics and app functionality.
                </Text>
                <Text weight={400} size={18} mt={10}>
                  <strong>With Your ATT Permission:</strong> If you grant App Tracking
                  Transparency permission, we may link your IDFA with your app usage data to:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Attribute app installs and in-app actions to specific advertising campaigns
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Measure the effectiveness of our Google Ads and Apple Search Ads campaigns
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Analyze user behavior and conversion funnels across different ad campaigns
                    </Text>
                  </List.Item>
                </List>
                <br />
                <Text weight={400} size={18}>
                  <strong>What We Do NOT Use Your Data For:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Cross-app or cross-site tracking (tracking you across different apps or
                      websites for behavioral profiling)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Linking your data with third-party data brokers or data aggregators
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Creating user profiles for third-party advertising networks
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Selling your data to other companies
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>Your Choice Matters:</strong> Whether you allow or deny ATT permission,
                  Mineralis will continue to work exactly the same. Your choice only affects how we
                  measure advertising campaign effectiveness:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>If you allow:</strong> We use IDFA for more accurate ad attribution
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>If you deny:</strong> We use SKAdNetwork for privacy-preserving
                      attribution (no IDFA access)
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  You can change your ATT preference at any time in iOS Settings {"->"} Privacy{" "}
                  {"->"} Tracking.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  5. Service Providers and Analytics Partners
                </Text>
                <Text weight={400} size={18}>
                  We use the following third-party service providers to help us operate Mineralis and
                  analyze usage:
                </Text>

                <Text weight={600} size={20} mt={15}>
                  5.1 Firebase Analytics (by Google)
                </Text>
                <Text weight={400} size={18}>
                  We use Firebase Analytics to collect and analyze app usage data. Firebase acts as
                  a data processor on our behalf and is bound by data processing agreements.
                </Text>
                <Text weight={400} size={18}>
                  Learn more:{" "}
                  <Anchor href="https://firebase.google.com/support/privacy" target="_blank" sx={{ color: "#5622F4" }}>
                    https://firebase.google.com/support/privacy
                  </Anchor>
                </Text>

                <Text weight={600} size={20} mt={15}>
                  5.2 Mixpanel
                </Text>
                <Text weight={400} size={18}>
                  We use Mixpanel for product analytics and user behavior analysis. Mixpanel
                  processes data according to our instructions and their privacy policy.
                </Text>
                <Text weight={400} size={18}>
                  Learn more:{" "}
                  <Anchor href="https://mixpanel.com/legal/privacy-policy/" target="_blank" sx={{ color: "#5622F4" }}>
                    https://mixpanel.com/legal/privacy-policy/
                  </Anchor>
                </Text>

                <Text weight={600} size={20} mt={15}>
                  5.3 Adapty
                </Text>
                <Text weight={400} size={18}>
                  We use Adapty for subscription management, analytics, and Apple Search Ads
                  attribution. Adapty helps us manage in-app purchases and understand subscription
                  metrics.
                </Text>
                <Text weight={400} size={18}>
                  Learn more:{" "}
                  <Anchor href="https://adapty.io/privacy/" target="_blank" sx={{ color: "#5622F4" }}>
                    https://adapty.io/privacy/
                  </Anchor>
                </Text>

                <Text weight={400} size={18} mt={15}>
                  <strong>Important:</strong> These service providers have access to your data only
                  to perform services on our behalf and are obligated not to disclose or use it for
                  any other purpose. They act as data processors under our data processing
                  agreements.
                </Text>

                <Text weight={600} size={20} mt={15}>
                  5.4 Google Ads and SKAdNetwork
                </Text>
                <Text weight={400} size={18}>
                  We use Google Ads to promote Mineralis and measure the effectiveness of our
                  advertising campaigns. To enable accurate attribution, we use Apple's SKAdNetwork
                  framework.
                </Text>
                <Text weight={400} size={18} mt={10}>
                  <strong>How SKAdNetwork Works with Google Ads:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      When you tap on a Google Ad and install Mineralis, a cryptographically signed
                      postback is sent from Apple's servers to Google after a delay (24+ hours)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      The postback contains only the conversion value and campaign ID — no personal
                      information
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      This allows Google to attribute installs and conversions to specific ads
                      without receiving any user-identifiable data
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      SKAdNetwork postbacks are sent directly from Apple to the ad network —
                      Mineralis never sees or handles this data
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>Conversion Values We Track:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Install (0):</strong> App was installed
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Trial Started (1):</strong> User started a free trial
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>First Scan (2):</strong> User completed their first mineral identification
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>First Purchase (3):</strong> User made their first purchase (trial
                      conversion or direct purchase)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Renewal (4):</strong> Subscription renewed
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>High Value (5):</strong> Long-term subscriber with multiple renewals
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  Learn more about SKAdNetwork:{" "}
                  <Anchor
                    href="https://developer.apple.com/documentation/storekit/skadnetwork"
                    target="_blank"
                    sx={{ color: "#5622F4" }}
                  >
                    Apple SKAdNetwork Documentation
                  </Anchor>
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  6. Data Storage and Retention
                </Text>
                <Text weight={600} size={20} mt={15}>
                  6.1 Local Data Storage
                </Text>
                <Text weight={400} size={18}>
                  <strong>Collection Data:</strong> All your saved stones, mineral notes, wishlists,
                  and settings are stored locally on your device using Apple's SwiftData framework.
                  This data:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Remains on your device and is NOT uploaded to our servers
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Is protected by your device's security features (encryption, passcode, Face
                      ID/Touch ID)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Can be backed up through iCloud if you have iCloud backup enabled (controlled
                      by your iOS settings)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Is deleted when you delete the app from your device
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  6.2 Analytics Data Retention
                </Text>
                <Text weight={400} size={18}>
                  Analytics data (events, user properties) is retained by our service providers
                  according to their retention policies:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Firebase Analytics: Retains data for up to 14 months
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Mixpanel: Retains data according to your plan (typically 5–7 years for paid
                      plans)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Adapty: Retains subscription data for the duration of your account plus legal
                      retention requirements
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      SKAdNetwork: Data is NOT stored by Mineralis. SKAdNetwork postbacks are sent
                      directly from Apple to ad networks after a 24+ hour delay. Mineralis never
                      receives or stores this attribution data
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  6.3 User ID Persistence
                </Text>
                <Text weight={400} size={18}>
                  Your User ID (UUID) is stored in your device's Keychain and persists until:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      You delete the app from your device, or
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      You explicitly request deletion through our support channel
                    </Text>
                  </List.Item>
                </List>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  7. Your Rights and Choices
                </Text>
                <Text weight={400} size={18}>
                  Depending on your location, you may have the following rights regarding your
                  personal data:
                </Text>

                <Text weight={600} size={20} mt={15}>
                  7.1 Access and Portability
                </Text>
                <Text weight={400} size={18}>
                  You can request access to the analytics data we have collected about your User ID.
                  Contact us at{" "}
                  <Anchor href="mailto:privacy@mineralis.app" sx={{ color: "#5622F4" }}>
                    privacy@mineralis.app
                  </Anchor>{" "}
                  to request a copy of your data.
                </Text>

                <Text weight={600} size={20} mt={15}>
                  7.2 Deletion
                </Text>
                <Text weight={400} size={18}>
                  You have the right to request deletion of your data:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Local Data:</strong> You can delete all local data (stones, notes,
                      wishlists) by deleting the app from your device
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Analytics Data:</strong> Contact us at{" "}
                      <Anchor href="mailto:privacy@mineralis.app" sx={{ color: "#5622F4" }}>
                        privacy@mineralis.app
                      </Anchor>{" "}
                      to request deletion of your User ID and associated analytics data
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  7.3 Opt-Out of Tracking and Analytics
                </Text>
                <Text weight={400} size={18}>
                  <strong>App Tracking Transparency (ATT) Settings:</strong>
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      You can deny or revoke ATT permission at any time in iOS Settings {"->"}{" "}
                      Privacy {"->"} Tracking
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      When you disable tracking, we will stop accessing your IDFA for advertising
                      attribution
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Even with tracking disabled, Mineralis will continue to work normally
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18} mt={10}>
                  <strong>Analytics Opt-Out:</strong> Currently, Mineralis does not offer an in-app
                  opt-out for first-party analytics (Firebase, Mixpanel). If you wish to stop all
                  analytics collection, you can delete the app.
                </Text>

                <Text weight={600} size={20} mt={15}>
                  7.4 California Residents (CCPA)
                </Text>
                <Text weight={400} size={18}>
                  If you are a California resident, you have additional rights under the California
                  Consumer Privacy Act (CCPA):
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to know what personal information we collect and how we use it
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to delete your personal information
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to opt-out of the "sale" of your personal information (Note: We do NOT
                      sell your personal information)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to non-discrimination for exercising your CCPA rights
                    </Text>
                  </List.Item>
                </List>

                <Text weight={600} size={20} mt={15}>
                  7.5 European Residents (GDPR)
                </Text>
                <Text weight={400} size={18}>
                  If you are located in the European Economic Area (EEA), you have rights under the
                  General Data Protection Regulation (GDPR):
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right of access, rectification, and erasure
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to restrict or object to processing
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to data portability
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to withdraw consent at any time
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Right to lodge a complaint with a supervisory authority
                    </Text>
                  </List.Item>
                </List>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  8. Children's Privacy
                </Text>
                <Text weight={400} size={18}>
                  Mineralis is not directed to children under the age of 13 (or 16 in the EEA). We do
                  not knowingly collect personal information from children. If you are a parent or
                  guardian and believe your child has provided us with personal information, please
                  contact us at{" "}
                  <Anchor href="mailto:privacy@mineralis.app" sx={{ color: "#5622F4" }}>
                    privacy@mineralis.app
                  </Anchor>
                  , and we will take steps to delete such information.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  9. International Data Transfers
                </Text>
                <Text weight={400} size={18}>
                  Mineralis is operated from the United States, and our service providers (Firebase,
                  Mixpanel, Adapty) may process data in the United States or other countries. If you
                  are located outside the United States, please be aware that your information may
                  be transferred to, stored, and processed in countries where our service providers
                  operate.
                </Text>
                <br />
                <Text weight={400} size={18}>
                  We ensure that such transfers comply with applicable data protection laws,
                  including through the use of Standard Contractual Clauses approved by the European
                  Commission where applicable.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  10. Security
                </Text>
                <Text weight={400} size={18}>
                  We take reasonable measures to protect your information from unauthorized access,
                  use, or disclosure:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Local Data:</strong> Your stones, notes, and collections are protected
                      by iOS device security (encryption, device passcode, biometric authentication)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>User ID:</strong> Stored securely in device Keychain with restricted
                      access
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Data Transmission:</strong> Analytics data and AI identification
                      requests are transmitted using encrypted connections (HTTPS/TLS)
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      <strong>Service Providers:</strong> We only work with reputable service
                      providers that maintain strong security practices
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18}>
                  However, no method of transmission over the Internet or electronic storage is 100%
                  secure. While we strive to use commercially acceptable means to protect your
                  information, we cannot guarantee its absolute security.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  11. Changes to This Privacy Policy
                </Text>
                <Text weight={400} size={18}>
                  We may update this Privacy Policy from time to time to reflect changes in our
                  practices, services, or legal requirements. We will notify you of any material
                  changes by:
                </Text>
                <List withPadding>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Posting the updated Privacy Policy on this page
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Updating the "Last Updated" date at the top of this policy
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text weight={400} size={18}>
                      Providing an in-app notification if the changes are significant
                    </Text>
                  </List.Item>
                </List>
                <Text weight={400} size={18}>
                  You are advised to review this Privacy Policy periodically for any changes.
                  Changes to this Privacy Policy are effective when they are posted on this page.
                </Text>
              </Stack>

              <Stack spacing={0} justify="left">
                <Text weight={600} size={26}>
                  12. Contact Us
                </Text>
                <Text weight={400} size={18}>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or
                  our data practices, please contact us:
                </Text>
                <br />
                <Text weight={400} size={18}>
                  <strong>Email:</strong>{" "}
                  <Anchor href="mailto:privacy@mineralis.app" sx={{ color: "#5622F4" }}>
                    privacy@mineralis.app
                  </Anchor>
                </Text>
                <br />
                <Text weight={400} size={18}>
                  <strong>Data Protection Requests:</strong> For requests to access, delete, or port
                  your data, please email us at{" "}
                  <Anchor href="mailto:privacy@mineralis.app" sx={{ color: "#5622F4" }}>
                    privacy@mineralis.app
                  </Anchor>{" "}
                  with "Data Request" in the subject line. We will respond to your request within 30
                  days.
                </Text>
                <br />
                <Text weight={400} size={18}>
                  <strong>App Developer:</strong> Anatoli Niculeac
                </Text>
                <br />
                <Text weight={400} size={18}>
                  We are committed to working with you to obtain a fair resolution of any complaint
                  or concern about privacy. If you believe we have not been able to assist with your
                  complaint or concern, and you are located in the EEA, you have the right to lodge
                  a complaint with the competent supervisory authority.
                </Text>
              </Stack>

            </Box>
            <Space h={30} />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.containerFooter}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={30}>
            <Box className={classes.sectionTwo}>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                Made with love by Anatoli Niculeac<sup>&copy;</sup> 2026
              </Text>
              <Group spacing={10} p={20} position="right">
                <Anchor component={Link} to={"/support"} className={classes.link}>
                  <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                    Support
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>|</Text>
                <Anchor component={Link} to={"/terms"} className={classes.link}>
                  <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                    Terms of use
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>|</Text>
                <Anchor component={Link} to={"/privacy"} className={classes.link}>
                  <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                    Privacy Policy
                  </Text>
                </Anchor>
                <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>|</Text>
                <Anchor component={Link} to={"/contact"} className={classes.link}>
                  <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                    Contact
                  </Text>
                </Anchor>
              </Group>
              <Space h={10} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default PrivacyPage;

const useStyles = createStyles({
  root: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },
  container: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #EDE8FE 0%, #F0ECFF 100%)",
  },
  containerTwo: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    backgroundColor: "#FFFFFF",
  },
  containerFooter: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: "100%",
    "@media (max-width: 768px)": { width: "100%" },
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, #F0ECFF 0%, #EDE8FE 100%)",
  },
  section: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: 25,
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  sectionTwo: {
    maxWidth: 980,
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 25,
    paddingRight: 25,
  },
  grid: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateColumns: "auto",
    gridTemplateRows: "auto",
    "@media (max-width: 1023px)": {
      gridTemplateColumns: "auto",
      justifyContent: "stretch",
    },
    "@media (min-width: 1600px)": {
      gridTemplateColumns: "auto",
      gridTemplateRows: "auto",
      WebkitTransition: "300ms",
    },
  },
  link: {
    whiteSpace: "nowrap",
    textDecoration: "none",
    ":last-child": { borderRight: "none" },
    transition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    ":hover": {
      textDecoration: "none",
      opacity: 0.7,
    },
  },
});
